import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import {
  NetworkFailureKind,
  NetworkFailureReporter,
} from '@qtek/shared/models';

const THROTTLE_MS = 5 * 60 * 1000;

/**
 * Reports "request to our own backend failed at the network layer" to
 * Datadog RUM. The caller classifies the failure (blocked endpoint vs
 * dead backend vs offline) using the shared healthcheck probe.
 *
 * Only synthetic context is sent: method, templated path (no ids, no query),
 * request size and the failure kind — never bodies, file names or headers.
 */
@Injectable({ providedIn: 'root' })
export class DatadogNetworkFailureReporter implements NetworkFailureReporter {
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly lastReportedAt = new Map<string, number>();

  report(
    request: HttpRequest<unknown>,
    response: HttpErrorResponse,
    kind: NetworkFailureKind
  ): void {
    if (!this.environment.dataDogRumEnabled) {
      return;
    }

    const path = templatePath(request.url);
    const method = request.method;
    const requestBytes = requestSize(request.body);
    const status = response.status;
    const swActive = !!navigator.serviceWorker?.controller;

    const key = `${kind}|${method}|${path}`;
    const now = Date.now();

    if (now - (this.lastReportedAt.get(key) ?? -Infinity) < THROTTLE_MS) {
      return;
    }

    this.lastReportedAt.set(key, now);
    datadogRum.addError(new Error('backend_network_failure'), {
      kind,
      method,
      path,
      requestBytes,
      status,
      swActive,
    });
  }
}

const ID_SEGMENT = /\d/;
const VERSION_SEGMENT = /^v\d+$/;

/** `https://h/api/v2/deals/123?x=1` → `/api/v2/deals/:id` (host and query dropped). */
export function templatePath(url: string): string {
  let pathname: string;

  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return 'unknown';
  }

  return pathname
    .split('/')
    .map(segment =>
      ID_SEGMENT.test(segment) && !VERSION_SEGMENT.test(segment)
        ? ':id'
        : segment
    )
    .join('/');
}

/** Payload size in bytes without reading content; `undefined` when unknown. */
export function requestSize(body: unknown): number | undefined {
  if (typeof body === 'string') {
    return body.length;
  }

  if (body instanceof Blob) {
    return body.size;
  }

  if (body instanceof ArrayBuffer) {
    return body.byteLength;
  }

  if (body instanceof FormData) {
    let total = 0;
    body.forEach(value => {
      total += value instanceof Blob ? value.size : String(value).length;
    });

    return total;
  }

  return undefined;
}
